import React, { useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import type { ReportQueryInput, ReportsTipsByEmployeeData } from "src/features/Reports/types/report.types";
import { REPORTS_TIPS_BY_EMPLOYEE } from "src/constants/queries";
import { BtnLoader } from "src/Loader/index";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";

const headerCellClass =
  "py-3 align-middle text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] bg-[#f1f5f9] whitespace-nowrap";

interface TipsByEmployeeReportPageProps {
  embedded?: boolean;
  input?: ReportQueryInput | null;
}

export default function TipsByEmployeeReportPage({ embedded, input: inputProp }: TipsByEmployeeReportPageProps = {}) {
  const { hasRestaurant } = useAuth();
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const range = useMemo(() => getReportDateRange(datePreset), [datePreset]);
  const inputBuilt = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? { storeCorrelationId: hasRestaurant.correlationId, from: range.from, to: range.to }
        : null,
    [hasRestaurant?.correlationId, range.from, range.to]
  );
  const input = embedded ? inputProp ?? null : inputBuilt;
  const { data, loading, error } = useReportQuery<ReportsTipsByEmployeeData>(
    REPORTS_TIPS_BY_EMPLOYEE,
    "reportsTipsByEmployee",
    input
  );

  const rows = data?.rows ?? [];

  const kpiRow = (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-[10px] mb-4">
      <div className="rounded-[14px] bg-white p-[10px] border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <p className="text-[14px] text-[#6b7280] mb-1">Total Tips</p>
        <p className="text-lg font-semibold text-[#111827]">
          {loading ? "—" : formatCentsToDollarsWithDollarSign(data?.totalTipsCents ?? 0)}
        </p>
      </div>
    </div>
  );

  const content = (
    <>
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <BtnLoader />
        </div>
      ) : (
        <div className="w-full bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-x-auto">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse table-auto">
              <thead>
                <tr>
                  <th className={`pl-4 pr-2 ${headerCellClass}`}>Employee</th>
                  <th className={`px-2 ${headerCellClass}`}>Tips</th>
                  <th className={`px-2 ${headerCellClass}`}>Orders</th>
                  <th className={`px-2 ${headerCellClass}`}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-[#6b7280] text-sm">
                      No data for this period.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => (
                    <tr
                      key={i}
                      className="border-t border-[#f1f5f9] text-sm text-[#111827] hover:bg-[#F9FAFB]"
                    >
                      <td className="py-2 pl-4 pr-2">{row.employeeName ?? "—"}</td>
                      <td className="py-2 px-2">{formatCentsToDollarsWithDollarSign(row.totalTipsCents ?? 0)}</td>
                      <td className="py-2 px-2">{row.ordersCount ?? "—"}</td>
                      <td className="py-2 px-2">{row.percentOfTotal != null ? `${Number(row.percentOfTotal).toFixed(1)}%` : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <>
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}
        {content}
      </>
    );
  }
  return (
    <ReportPageLayout
      hideTitle
      datePreset={datePreset}
      onDatePresetChange={(preset) => setDatePreset(preset)}
      kpiRow={kpiRow}
      error={error}
    >
      {content}
    </ReportPageLayout>
  );
}
